import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import SectionHeading from '../SectionHeading';
import { fadeUp, stagger, viewport } from '../motion';

function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sendMessage = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const toastOption = {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    };

    const formData = {
      Name: name,
      Email: email,
      Message: message,
    };

    try {
      const response = await fetch("https://getform.io/f/31798245-609f-4194-9a22-c45f02c90b82", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setEmail("");
        setName("");
        setMessage("");
        toast.success("Message Sent Successfully", toastOption);
      } else {
        toast.error("Failed to Send Message");
      }
    } catch (err) {
      toast.error("Failed to Send Message");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div id="contact" className="py-20 w-full relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-20 right-10 w-40 h-40 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob-slow"></div>
        <div className="absolute bottom-10 left-10 w-32 h-32 bg-indigo-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob-medium"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <SectionHeading eyebrow="Say hello" title="Contact Me" className="mb-16">
            Have a question or want to work together? Feel free to reach out.
          </SectionHeading>

          <motion.div
            className="mx-auto bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden w-full sm:w-4/5 lg:w-2/3 relative"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            whileHover={{ y: -5, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)' }}
          >
            {/* Top Accent Bar */}
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-600 to-indigo-700"></div>

            <motion.div
              className="p-10"
              variants={stagger(0.12, 0.15)}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
            >
              <form
                action="https://getform.io/f/31798245-609f-4194-9a22-c45f02c90b82"
                method="POST"
                onSubmit={sendMessage}
              >
                <motion.div 
                  className="mb-6"
                  variants={fadeUp}
                >
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wider font-sans">
                    Name
                  </label>
                  <motion.input
                    type="text"
                    id="name"
                    name="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 font-sans text-gray-800"
                    placeholder="Enter your name"
                    required
                    whileFocus={{ scale: 1.005 }}
                  />
                </motion.div>

                <motion.div 
                  className="mb-6"
                  variants={fadeUp}
                >
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wider font-sans">
                    Email
                  </label>
                  <motion.input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 font-sans text-gray-800"
                    placeholder="Enter your email"
                    required
                    whileFocus={{ scale: 1.005 }}
                  />
                </motion.div>

                <motion.div 
                  className="mb-6"
                  variants={fadeUp}
                >
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wider font-sans">
                    Message
                  </label>
                  <motion.textarea
                    id="message"
                    name="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 font-sans text-gray-800"
                    placeholder="Enter your message"
                    rows="5"
                    required
                    whileFocus={{ scale: 1.005 }}
                  ></motion.textarea>
                </motion.div>

                <motion.div 
                  className="text-center mt-8"
                  variants={fadeUp}
                >
                  <motion.button
                    type="submit"
                    className="inline-flex items-center justify-center px-8 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white font-semibold rounded-full shadow-lg transition-all duration-300 hover:scale-105 font-sans"
                    whileTap={{ scale: 0.95 }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Send Message
                        <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1.946 9.315c-.522-.174-.527-.455.01-.634L21.075.061c.538-.18.811.088.628.638L15.602 19.34c-.183.55-.495.558-.696.018l-3.02-8.125-8.125-3.02z" />
                        </svg>
                      </span>
                    )}
                  </motion.button>
                </motion.div>
              </form>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default Contact;